import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import DialogActions from "@material-ui/core/DialogActions";
import MuiDialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import Button from "@material-ui/core/Button";

import { getVintedUser } from "src/utils/extension/vinted";
import type { Cookie } from "src/utils/extension";
import {
  getVintedIntegration,
  vintedIntegrationCreate,
} from "src/apiService/modules/vinted";
import { AddNewSalePlatformIntegrationButton } from "src/components/AddNewSalePlatformIntegrationDialog";

import { StatefulLoadableButton } from "src/components/LoadableButton";
import DialogTitle from "./DialogTitle";
import {
  completeUserSetupQuestionCardPlatform,
  doesUserHaveCardPlatform,
} from "src/apiService/modules/users";
import { useDispatch } from "react-redux";
import { vintedSyncSales } from "src/store/integrationSync/actions/vinted";
import MonthYear from "src/interfaces/monthYear.interface";
import { useRecordEvent } from "src/hooks/useRecordEvent";

interface VintedConfirmProps {
  onClose: () => void;
  showConnectSalePlatform?: boolean;
}

function VintedConfirm({
  onClose,
  showConnectSalePlatform = true,
}: VintedConfirmProps) {
  const dispatch = useDispatch();
  const recordEvent = useRecordEvent();
  const [vintedUserData, setVintedUserData] = useState<
    | {
        username: string;
        userId: string;
        cookies: Cookie[];
      }
    | { error: string }
    | { loading: true }
  >({ loading: true });

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const data = await getVintedUser();
        if (cancel) return;
        setVintedUserData(data);
      } catch (e) {
        if (cancel) return;
        setVintedUserData({ error: (e as Error).message });
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  const onConnect = async (bankAccountId?: string) => {
    if ("cookies" in vintedUserData && vintedUserData.cookies) {
      const existingVintedIntegration = await getVintedIntegration();
      if (existingVintedIntegration?.id) {
        if (await doesUserHaveCardPlatform("Vinted")) {
          await completeUserSetupQuestionCardPlatform("Vinted");
          const currentDate = new Date();
          dispatch(
            vintedSyncSales(existingVintedIntegration.id, {
              start: {
                month: 1,
                year: currentDate.getFullYear() - 1,
              },
              end: {
                month: currentDate.getMonth() + 1,
                year: currentDate.getFullYear(),
              } as MonthYear,
            })
          );
        }
        toast.info("That Vinted account is already connected.");
        onClose();
        return;
      }

      await vintedIntegrationCreate({
        cookies: vintedUserData.cookies,
        username: vintedUserData.username,
        userId: vintedUserData.userId,
        lastSync: new Date().toISOString(),
        bankAccountId,
      });
      recordEvent({
        type: "feature-adoption",
        feature: "integration-added",
        element: "sale-platform-vinted",
      });
      if (await doesUserHaveCardPlatform("Vinted")) {
        const vintedIntegration = await getVintedIntegration();
        await completeUserSetupQuestionCardPlatform("Vinted");
        if (vintedIntegration?.id) {
          const currentDate = new Date();
          dispatch(
            vintedSyncSales(vintedIntegration.id, {
              start: {
                month: 1,
                year: currentDate.getFullYear() - 1,
              },
              end: {
                month: currentDate.getMonth() + 1,
                year: currentDate.getFullYear(),
              } as MonthYear,
            })
          );
        }
      }
      onClose();
    }
  };

  return (
    <>
      <DialogTitle onClose={onClose}>Add Vinted integration</DialogTitle>
      <MuiDialogContent>
        <DialogContentText>
          {"username" in vintedUserData
            ? `The Vinted account "${vintedUserData.username}" will be added.`
            : "error" in vintedUserData
            ? `ERROR: ${vintedUserData.error}`
            : "Loading..."}
        </DialogContentText>
      </MuiDialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {showConnectSalePlatform ? (
          <AddNewSalePlatformIntegrationButton
            variant="contained"
            disabled={
              !!(vintedUserData as any).error || (vintedUserData as any).loading
            }
            onConnect={onConnect}
            color="primary"
            autoFocus
          >
            Add
          </AddNewSalePlatformIntegrationButton>
        ) : (
          <StatefulLoadableButton
            color="primary"
            variant="contained"
            disabled={
              !!(vintedUserData as any).error || (vintedUserData as any).loading
            }
            onClick={async () => await onConnect()}
          >
            Connect
          </StatefulLoadableButton>
        )}
      </DialogActions>
    </>
  );
}

export default VintedConfirm;
